import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { useEstateContext } from "@/lib/EstateContext";
import { estatesService } from "@/services";
import { createPageUrl } from "@/utils";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Building2, Loader2, AlertCircle, CheckCircle2, UserPlus } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

export default function JoinEstate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isLoadingAuth } = useAuth();
  const { refreshEstates, setActiveEstate } = useEstateContext();

  const code = searchParams.get("code")?.toUpperCase() ?? "";

  const [inviteInfo, setInviteInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [infoError, setInfoError] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [joined, setJoined] = useState(false);

  // Redirect to login (preserving code in redirect URL) if not authenticated
  useEffect(() => {
    if (isLoadingAuth) return;
    if (!user) {
      navigate(`/Login?redirect=/JoinEstate?code=${code}`, { replace: true });
    }
  }, [user, isLoadingAuth, code, navigate]);

  // Load invite info once we have a code and user
  useEffect(() => {
    if (!code || !user) return;

    const load = async () => {
      setLoadingInfo(true);
      setInfoError("");
      try {
        const data = await estatesService.getInviteInfo(code);
        if (!data) {
          setInfoError("Invite not found. Please check the link and try again.");
          return;
        }
        if (data.accepted_at) {
          setInfoError("This invite has already been used.");
          return;
        }
        if (new Date(data.expires_at) < new Date()) {
          setInfoError("This invite has expired. Please ask the estate owner for a new link.");
          return;
        }
        setInviteInfo(data);
      } catch {
        setInfoError("Could not load invite details. Please check the link.");
      } finally {
        setLoadingInfo(false);
      }
    };

    load();
  }, [code, user]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const estate = await estatesService.acceptInvite(code, user.id);
      await refreshEstates();
      setActiveEstate(estate.id);
      setJoined(true);
      toast.success(`You've joined ${estate.name}!`);
      setTimeout(() => {
        navigate(`${createPageUrl("EstateDashboard")}?estate=${estate.id}`);
      }, 1500);
    } catch (err) {
      toast.error(err.message || "Could not join estate. Please try again.");
    } finally {
      setAccepting(false);
    }
  };

  // Loading auth
  if (isLoadingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <Card className="p-8 shadow-2xl bg-white/95 backdrop-blur-sm">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <UserPlus className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Estate Invitation</h1>
            <p className="text-slate-600 text-sm mt-1">You've been invited to manage an estate</p>
          </div>

          {/* No code */}
          {!code && (
            <div className="text-center py-4">
              <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-2" />
              <p className="text-slate-700 font-medium">No invite code provided.</p>
              <p className="text-slate-500 text-sm mt-1">Please use the full invite link you were given.</p>
            </div>
          )}

          {/* Loading invite info */}
          {code && loadingInfo && (
            <div className="flex items-center justify-center py-8 gap-3 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Loading invite details…</span>
            </div>
          )}

          {/* Error state */}
          {code && !loadingInfo && infoError && (
            <div className="text-center py-4">
              <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-2" />
              <p className="text-slate-700 font-medium">{infoError}</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => navigate(createPageUrl("EstateDashboard"))}
              >
                Go to Dashboard
              </Button>
            </div>
          )}

          {/* Success state after joining */}
          {joined && (
            <div className="text-center py-4">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-slate-900 font-semibold text-lg">You're in!</p>
              <p className="text-slate-500 text-sm mt-1">Redirecting to your dashboard…</p>
            </div>
          )}

          {/* Invite preview + accept button */}
          {code && !loadingInfo && !infoError && inviteInfo && !joined && (
            <>
              <div className="bg-slate-50 rounded-2xl p-5 mb-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-green-700" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-lg leading-tight">{inviteInfo.estate?.name}</p>
                    {inviteInfo.estate?.location && (
                      <p className="text-sm text-slate-500">{inviteInfo.estate.location}</p>
                    )}
                  </div>
                </div>
                {inviteInfo.inviter && (
                  <p className="text-sm text-slate-600">
                    Invited by{" "}
                    <span className="font-medium text-slate-800">
                      {inviteInfo.inviter.full_name || inviteInfo.inviter.email}
                    </span>
                  </p>
                )}
              </div>

              <Button
                onClick={handleAccept}
                disabled={accepting}
                className="w-full bg-[#1883FF] hover:bg-[#0066CC] text-white rounded-[40px] py-3 font-semibold text-base"
              >
                {accepting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Joining…</>
                ) : (
                  "Accept & Join Estate"
                )}
              </Button>
              <p className="text-xs text-center text-slate-400 mt-3">
                You'll be added as an estate manager. Nothing is shared publicly.
              </p>
            </>
          )}
        </Card>
      </motion.div>
    </div>
  );
}
